import type { VideoAssignment, VideoAssignee, Tutorial, TutorialVersion } from '@platform/shared-types';
import { tutorialCall as call } from './tutorialApi';

// Admin client for Video Tasks (academy-ext-service). Shares tutorialApi's
// host resolution, auth header and TutorialApiError surfacing via
// `tutorialCall` -- one code path, not a copy.

export interface VideoTaskCounts { open: number; waitingReview: number; overdue: number; approvedThisMonth: number }
export interface LibraryItem { tutorial: Tutorial; versions: TutorialVersion[]; totalBytes: number; assignment?: VideoAssignment }
export interface MeterRow { pk: string; sk: string; bytes: number; files: number; videos: number }

const t = (id: string) => `/admin/video-tasks/${encodeURIComponent(id)}`;

export const videoTasksApi = {
  list: () => call<{ assignments: VideoAssignment[]; counts: VideoTaskCounts }>('/admin/video-tasks'),
  create: (input: Partial<VideoAssignment>) =>
    call<{ assignment: VideoAssignment }>('/admin/video-tasks', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<VideoAssignment>) =>
    call<{ assignment: VideoAssignment }>(t(id), { method: 'PUT', body: JSON.stringify(patch) }),
  assign: (id: string, assignee: VideoAssignee, dueAt?: number) =>
    call<{ assignment: VideoAssignment }>(`${t(id)}/assign`, { method: 'POST', body: JSON.stringify({ assignee, dueAt }) }),
  requestChanges: (id: string, note: string) =>
    call<{ assignment: VideoAssignment }>(`${t(id)}/request-changes`, { method: 'POST', body: JSON.stringify({ note }) }),
  approve: (id: string) => call<{ assignment: VideoAssignment }>(`${t(id)}/approve`, { method: 'POST' }),
  cancel: (id: string) => call<{ assignment: VideoAssignment }>(`${t(id)}/cancel`, { method: 'POST' }),
  library: () => call<{ items: LibraryItem[] }>('/admin/library'),
  storage: () => call<{ meters: MeterRow[] }>('/admin/library/storage'),
  updateMeta: (tutorialId: string, patch: { title?: string; tags?: string[]; adWorthy?: boolean }) =>
    call<{ tutorial: Tutorial }>(`/admin/tutorials/${encodeURIComponent(tutorialId)}/meta`, { method: 'PUT', body: JSON.stringify(patch) }),
  purge: (tutorialId: string) =>
    call<{ tutorial: Tutorial; deleted: string[]; failed: string[] }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/purge`, { method: 'POST' },
    ),
};
